import type {
    IaScheduleAdjustments,
    InvestmentInTaxiCabsAndLicensesWorkingPaper,
} from '@/services/auditService/interface/asset';
import type {IntangibleRow} from '@/services/auditService/interface/wp-row';
import {ITL_CATEGORY_ROW_KEYS} from '@/pages/components/audit/adjustment/adjust/dialogs/asset/itlTitleUtils';

export const ITL_SECTION_ROW_KEYS = ITL_CATEGORY_ROW_KEYS;

export type ItlSectionKey = typeof ITL_SECTION_ROW_KEYS[number];

const LICENCE_RE = /licen[cs]e/i;

/** GET-only WP-level schedule lists — never row-level `{amorChgAdjs, nbvCfAdjs}`. */
export function asItlScheduleAdjustments(adj: unknown): IaScheduleAdjustments | null {
    if (!adj || typeof adj !== 'object' || Array.isArray(adj)) return null;
    const o = adj as Record<string, unknown>;
    if ('amorChgAdjs' in o || 'nbvCfAdjs' in o) return null;
    const amorChgAdjusts = Array.isArray(o.amorChgAdjusts) && o.amorChgAdjusts.length > 0
        ? o.amorChgAdjusts as IaScheduleAdjustments['amorChgAdjusts']
        : null;
    const nbvCfAdjusts = Array.isArray(o.nbvCfAdjusts) && o.nbvCfAdjusts.length > 0
        ? o.nbvCfAdjusts as IaScheduleAdjustments['nbvCfAdjusts']
        : null;
    if (!amorChgAdjusts && !nbvCfAdjusts) return null;
    return {amorChgAdjusts, nbvCfAdjusts};
}

export const emptyItlRow = (id: number): IntangibleRow => ({
    id,
    date: null,
    item: '',
    costBf: null,
    addition: null,
    costDisposal: null,
    accAmorBf: null,
    amorChg: null,
    accDisposal: null,
});

export function defaultItlWorkingPaper(): InvestmentInTaxiCabsAndLicensesWorkingPaper {
    return {
        taxiLicenseRow: [],
        fullPayTaxiCabRow: [],
        hirePurchaseTaxiCabRow: [],
    };
}

/** Split legacy flat intangibleRows into three sections (GET migration on frontend). */
export function migrateLegacyItlRows(
    paper: InvestmentInTaxiCabsAndLicensesWorkingPaper,
): InvestmentInTaxiCabsAndLicensesWorkingPaper {
    const hasSections = ITL_SECTION_ROW_KEYS.some((k) => {
        const rows = paper[k];
        return Array.isArray(rows) && rows.length > 0;
    });
    if (hasSections) {
        return paper;
    }
    const legacy = paper.intangibleRows;
    if (!Array.isArray(legacy) || legacy.length === 0) {
        return paper;
    }
    const taxiLicenseRow: IntangibleRow[] = [];
    const fullPayTaxiCabRow: IntangibleRow[] = [];
    const hirePurchaseTaxiCabRow: IntangibleRow[] = [];
    for (const row of legacy) {
        if (!row) continue;
        const item = String(row.item ?? '');
        if (LICENCE_RE.test(item)) {
            taxiLicenseRow.push(row);
        } else if (/hire\s*purchase|hp/i.test(item)) {
            hirePurchaseTaxiCabRow.push(row);
        } else {
            fullPayTaxiCabRow.push(row);
        }
    }
    return {
        ...paper,
        taxiLicenseRow,
        fullPayTaxiCabRow,
        hirePurchaseTaxiCabRow,
    };
}

export function extractItlWorkingPaper(raw: unknown): InvestmentInTaxiCabsAndLicensesWorkingPaper {
    const base = (raw && typeof raw === 'object' ? raw : {}) as InvestmentInTaxiCabsAndLicensesWorkingPaper;
    return migrateLegacyItlRows({
        taxiLicenseRow: Array.isArray(base.taxiLicenseRow) ? base.taxiLicenseRow : [],
        fullPayTaxiCabRow: Array.isArray(base.fullPayTaxiCabRow) ? base.fullPayTaxiCabRow : [],
        hirePurchaseTaxiCabRow: Array.isArray(base.hirePurchaseTaxiCabRow) ? base.hirePurchaseTaxiCabRow : [],
        itlTitle: base.itlTitle,
        intangibleRows: base.intangibleRows,
        adjustments: asItlScheduleAdjustments(base.adjustments) ?? undefined,
        'WOKE_DONE': base['WOKE_DONE'],
    });
}

export function isItlDetailFromDatabase(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object') return false;
    const paper = extractItlWorkingPaper(detail);
    for (const key of ITL_SECTION_ROW_KEYS) {
        const rows = paper[key];
        if (Array.isArray(rows) && rows.some((r) => r?.id != null && Number(r.id) > 0)) {
            return true;
        }
    }
    const legacy = paper.intangibleRows;
    if (Array.isArray(legacy) && legacy.some((r) => r?.id != null && Number(r.id) > 0)) {
        return true;
    }
    return false;
}

export function stripItlRowForSave(row: IntangibleRow): IntangibleRow {
    const {
        costCf: _costCf,
        accAmorCf: _accAmorCf,
        nbvCf: _nbvCf,
        nbvBf: _nbvBf,
        adjustments: _adjustments,
        iaItemFromPrior: _iaItemFromPrior,
        iaSuppressLaterRoll: _iaSuppressLaterRoll,
        ...base
    } = row as IntangibleRow & {iaSuppressLaterRoll?: boolean};
    return base;
}

export function toItlWpSaveShell(
    paper: InvestmentInTaxiCabsAndLicensesWorkingPaper,
    pendingItlTitle?: Record<string, string> | null,
): Record<string, unknown> {
    const shell: Record<string, unknown> = {};
    for (const key of ITL_SECTION_ROW_KEYS) {
        const rows = paper[key] ?? [];
        shell[key] = rows.map(stripItlRowForSave);
    }
    if (pendingItlTitle && Object.keys(pendingItlTitle).length > 0) {
        shell.itlTitle = pendingItlTitle;
    }
    return shell;
}

export function isItlSectionEmpty(rows: IntangibleRow[] | null | undefined): boolean {
    if (!rows || rows.length === 0) return true;
    return rows.every((row) => {
        const adj = row.adjustments;
        const hasFieldAdj = !!(adj && (
            (Array.isArray(adj.amorChgAdjs) && adj.amorChgAdjs.length > 0)
            || (Array.isArray(adj.nbvCfAdjs) && adj.nbvCfAdjs.length > 0)
        ));
        if (hasFieldAdj) return false;
        const hasItem = row.item && String(row.item).trim() !== '';
        const hasCostBf = row.costBf != null && row.costBf !== 0;
        const hasAddition = row.addition != null && row.addition !== 0;
        return !hasItem || (!hasCostBf && !hasAddition);
    });
}

export function allItlRows(paper: InvestmentInTaxiCabsAndLicensesWorkingPaper): IntangibleRow[] {
    return ITL_SECTION_ROW_KEYS.flatMap((k) => paper[k] ?? []);
}
